import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { speakersApi } from '../../api/speakers.js';
import { engagementsApi } from '../../api/engagements.js';
import { eventsApi } from '../../api/events.js';
import { filesApi } from '../../api/files.js';
import { StatusBadge } from '../../components/common/StatusBadge.jsx';
import { LoadingSpinner } from '../../components/common/LoadingSpinner.jsx';
import { ErrorAlert } from '../../components/common/ErrorAlert.jsx';
import { Modal } from '../../components/common/Modal.jsx';
import { PortalInviteControls } from '../../components/common/PortalInviteControls.jsx';
import { FileInput } from '../../components/common/FileInput.jsx';
import { FormField, Select, Textarea } from '../../components/common/FormField.jsx';
import { formatDate } from '../../utils/dates.js';
import { SPEAKER_COMM_TYPES } from '../../utils/constants.js';
import { buildSpeakerMailtoLink } from '../../utils/mailto.js';
import { useSender } from '../../hooks/useSender.js';
import { usePageHeader } from '../../hooks/usePageHeader.jsx';

const PRE_BOOKING_STATUSES = ['Potential', 'Contacted', 'Interested'];

export function SpeakerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sender = useSender();
  const [speaker, setSpeaker] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  usePageHeader(
    speaker ? `${speaker.firstName} ${speaker.lastName}` : 'Speaker',
    speaker?.jobTitle ? `${speaker.jobTitle}${speaker.organization ? ' · ' + speaker.organization : ''}` : undefined
  );

  const [emailModal, setEmailModal] = useState(false);
  const [emailType, setEmailType] = useState(SPEAKER_COMM_TYPES[0]);
  const [bookModal, setBookModal] = useState(false);
  const [bookEventId, setBookEventId] = useState('');
  const [booking, setBooking] = useState(false);
  const [startingOnboarding, setStartingOnboarding] = useState(false);
  const [assignEventId, setAssignEventId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [editingBio, setEditingBio] = useState(false);
  const [bioDraft, setBioDraft] = useState('');
  const [savingBio, setSavingBio] = useState(false);

  const load = () => {
    Promise.all([
      speakersApi.get(id),
      engagementsApi.list({ speaker: id }),
      eventsApi.list(),
    ])
      .then(([s, e, evts]) => {
        setSpeaker(s);
        setEngagements(e ?? []);
        setEvents(Object.fromEntries((evts ?? []).map((ev) => [ev.id, ev])));
      })
      .catch(setError)
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const confirmedEvents = Object.values(events).filter((e) => e.status === 'Confirmed');

  const openMailClient = () => {
    window.location.href = buildSpeakerMailtoLink(emailType, speaker, confirmedEvents, sender);
    setEmailModal(false);
  };

  const handleBook = async () => {
    if (!bookEventId) return;
    setBooking(true);
    try {
      const eng = await engagementsApi.create({ speakerId: id, eventId: bookEventId });
      navigate(`/engagements/${eng.id}`);
    } catch (e) {
      setError(e.message);
      setBooking(false);
      setBookModal(false);
    }
  };

  // An "onboarding" engagement is one with no event yet — created so a
  // speaker can start filling out their portal (bio/headshot/agreement)
  // before the chapter has picked which event they'll speak at.
  const onboardingEngagement = engagements.find((e) => !e.eventId);

  const startOnboarding = async () => {
    setStartingOnboarding(true);
    try {
      await engagementsApi.create({ speakerId: id });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setStartingOnboarding(false);
    }
  };

  const assignEvent = async () => {
    if (!assignEventId || !onboardingEngagement) return;
    setAssigning(true);
    try {
      await engagementsApi.update(onboardingEngagement.id, { eventId: assignEventId });
      setAssignEventId('');
      load(); // status may have just been promoted to Active
    } catch (e) {
      setError(e.message);
    } finally {
      setAssigning(false);
    }
  };

  // Direct capture on the speaker's own page is the default way to get a
  // bio/headshot in — the portal submission-and-review flow still works
  // (via Onboarding below), but a coordinator shouldn't have to go through
  // it just to paste in a bio or drop in a photo they already have.
  const uploadHeadshot = async (file) => {
    setUploadingHeadshot(true);
    try {
      const { id: fileId } = await filesApi.upload(file);
      await speakersApi.update(id, { headshot: { fileId } });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploadingHeadshot(false);
    }
  };

  const saveBio = async () => {
    setSavingBio(true);
    try {
      await speakersApi.update(id, { bio: bioDraft });
      setEditingBio(false);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingBio(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!speaker) return <ErrorAlert error={error ?? 'Speaker not found'} />;

  const inPipeline = PRE_BOOKING_STATUSES.includes(speaker.status);

  return (
    <div>
      <div className="page-toolbar" style={{ justifyContent: 'flex-end' }}>
        <div className="inline">
          <button className="btn btn-secondary" onClick={() => setEmailModal(true)}>Send email</button>
          <button className="btn btn-primary" onClick={() => setBookModal(true)}>Book for an event</button>
          <Link to={`/speakers/${id}/edit`} className="btn btn-secondary">Edit</Link>
        </div>
      </div>
      <div className="page-body stack">
        <ErrorAlert error={error} onDismiss={() => setError(null)} />

        {/* One 4-column grid for every "quick fact" card, including
            onboarding's own cards — the goal is that a fully onboarded
            speaker's whole profile fits in two rows, nothing pushed below
            the fold. Speaking engagements (a table, not a quarter-width
            card) stays its own full-width section underneath. */}
        <div className="grid-4">
          <div className="card stack-2">
            <h2 style={{ fontSize: 'var(--text-base)' }}>Contact</h2>
            <dl style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 'var(--space-2) var(--space-3)', fontSize: 'var(--text-sm)' }}>
              {speaker.preferredName && <><dt style={{ color: 'var(--color-text-muted)' }}>Preferred</dt><dd>{speaker.preferredName}</dd></>}
              <dt style={{ color: 'var(--color-text-muted)' }}>Email</dt>
              <dd><a href={`mailto:${speaker.email}`}>{speaker.email}</a></dd>
              {speaker.phone && <><dt style={{ color: 'var(--color-text-muted)' }}>Phone</dt><dd>{speaker.phone}</dd></>}
              {speaker.websiteUrl && <><dt style={{ color: 'var(--color-text-muted)' }}>Web</dt><dd><a href={speaker.websiteUrl} target="_blank" rel="noreferrer">{speaker.websiteUrl}</a></dd></>}
              <dt style={{ color: 'var(--color-text-muted)' }}>Status</dt>
              <dd><StatusBadge status={speaker.status} type="speaker" /></dd>
            </dl>
          </div>
          <div className="card stack-2">
            <h2 style={{ fontSize: 'var(--text-base)' }}>Topics</h2>
            {speaker.topicAreas?.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {speaker.topicAreas.map((t) => <span key={t} className="badge badge-neutral">{t}</span>)}
              </div>
            ) : <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>No topics listed</p>}
          </div>

          {/* Pipeline — only meaningful while there's no active booking yet, but
              shown whenever any of these fields have data (a speaker can move
              back into the pipeline for a new opportunity even after Active). */}
          {(inPipeline || speaker.topic || speaker.source || speaker.lastContactDate || speaker.nextFollowUpDate) && (
            <div className="card stack-2">
              <h2 style={{ fontSize: 'var(--text-base)' }}>Pipeline</h2>
              <dl style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 'var(--space-2) var(--space-3)', fontSize: 'var(--text-sm)' }}>
                {speaker.topic && <><dt style={{ color: 'var(--color-text-muted)' }}>Topic</dt><dd>{speaker.topic}</dd></>}
                {speaker.source && <><dt style={{ color: 'var(--color-text-muted)' }}>Source</dt><dd>{speaker.source}</dd></>}
                {speaker.lastContactDate && <><dt style={{ color: 'var(--color-text-muted)' }}>Last contact</dt><dd>{formatDate(speaker.lastContactDate)}</dd></>}
                {speaker.nextFollowUpDate && <><dt style={{ color: 'var(--color-text-muted)' }}>Next follow-up</dt><dd>{formatDate(speaker.nextFollowUpDate)}</dd></>}
              </dl>
            </div>
          )}

          <div className="card stack-2">
            <h2 style={{ fontSize: 'var(--text-base)' }}>Headshot</h2>
            {speaker.headshot?.fileId && (
              <img
                src={filesApi.getUrl(speaker.headshot.fileId)}
                alt={`${speaker.firstName} ${speaker.lastName}`}
                style={{ maxWidth: 120, borderRadius: 'var(--radius-lg)' }}
              />
            )}
            <FileInput
              onFile={uploadHeadshot}
              accept="image/jpeg,image/png,image/webp"
              label={uploadingHeadshot ? 'Uploading…' : speaker.headshot?.fileId ? 'Replace photo' : 'Upload photo'}
              hint="JPG, PNG, or WebP"
            />
          </div>
          <div className="card stack-2">
            <div className="between">
              <h2 style={{ fontSize: 'var(--text-base)' }}>Biography</h2>
              {!editingBio && (
                <button className="btn btn-secondary btn-sm" onClick={() => { setBioDraft(speaker.bio ?? ''); setEditingBio(true); }}>
                  {speaker.bio ? 'Edit' : 'Add'}
                </button>
              )}
            </div>
            {editingBio ? (
              <>
                <Textarea value={bioDraft} onChange={(e) => setBioDraft(e.target.value)} rows={6} />
                <div className="inline">
                  <button className="btn btn-primary btn-sm" onClick={saveBio} disabled={savingBio}>
                    {savingBio ? 'Saving…' : 'Save'}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingBio(false)} disabled={savingBio}>Cancel</button>
                </div>
              </>
            ) : speaker.bio ? (
              <p className="prose" style={{ fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap' }}>{speaker.bio}</p>
            ) : (
              <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>No biography yet.</p>
            )}
          </div>
          <div className="card stack-2">
            <h2 style={{ fontSize: 'var(--text-base)' }}>Notes</h2>
            {speaker.notes ? (
              <p className="prose" style={{ fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap' }}>{speaker.notes}</p>
            ) : (
              <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>No notes.</p>
            )}
          </div>

          {onboardingEngagement ? (
            <>
              <PortalInviteControls
                engagement={onboardingEngagement}
                event={null}
                speaker={speaker}
                onUpdate={() => load()}
              />
              <div className="card stack-2">
                <h2 style={{ fontSize: 'var(--text-base)' }}>Assign to an event</h2>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                  Turns onboarding into a real booking{inPipeline && ' and moves status to Active'}.
                </p>
                <Select
                  value={assignEventId}
                  onChange={(e) => setAssignEventId(e.target.value)}
                  aria-label="Assign event"
                >
                  <option value="">Select event…</option>
                  {confirmedEvents.map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.name} ({ev.date})</option>
                  ))}
                </Select>
                <button className="btn btn-primary btn-sm" onClick={assignEvent} disabled={!assignEventId || assigning} style={{ alignSelf: 'flex-start' }}>
                  {assigning ? 'Assigning…' : 'Assign'}
                </button>
                {confirmedEvents.length === 0 && (
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-subtle)' }}>No confirmed events yet.</p>
                )}
              </div>
            </>
          ) : (
            <div className="card stack-2">
              <h2 style={{ fontSize: 'var(--text-base)' }}>Onboarding</h2>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                Send {speaker.firstName} a portal link for their biography, headshot, and signed
                agreement — before an event is even picked.
              </p>
              <button className="btn btn-primary btn-sm" onClick={startOnboarding} disabled={startingOnboarding} style={{ alignSelf: 'flex-start' }}>
                {startingOnboarding ? 'Starting…' : 'Start onboarding'}
              </button>
            </div>
          )}
        </div>

        <section>
          <h2 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-3)' }}>Speaking engagements</h2>
          {engagements.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
              No bookings yet. {confirmedEvents.length > 0 && 'Use "Book for an event" above to connect this speaker to an event.'}
            </p>
          ) : (
            <div className="data-table" style={{ overflowX: 'auto' }}>
              <table>
                <thead><tr><th>Talk</th><th>Status</th><th>Event</th></tr></thead>
                <tbody>
                  {engagements.map((e) => {
                    const ev = events[e.eventId];
                    return (
                      <tr key={e.id}>
                        <td><Link to={`/engagements/${e.id}`}>{e.talkTitle ?? '(untitled)'}</Link></td>
                        <td><StatusBadge status={e.status} /></td>
                        <td>{ev ? <Link to={`/events/${ev.id}/edit`}>{ev.name}</Link> : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <Modal
        open={emailModal}
        onClose={() => setEmailModal(false)}
        title="Send email"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setEmailModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={openMailClient}>Open in mail client</button>
          </>
        }
      >
        <div className="stack">
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Select a template. The draft will open in your mail client addressed to {speaker.email}, with subject and body pre-filled. You can edit before sending.
          </p>
          <FormField label="Template" id="speakerCommType">
            <Select id="speakerCommType" value={emailType} onChange={(e) => setEmailType(e.target.value)}>
              {SPEAKER_COMM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FormField>
        </div>
      </Modal>

      <Modal
        open={bookModal}
        onClose={() => setBookModal(false)}
        title="Book for an event"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setBookModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleBook} disabled={!bookEventId || booking}>
              {booking ? 'Booking…' : 'Book'}
            </button>
          </>
        }
      >
        <div className="stack">
          <p style={{ fontSize: 'var(--text-sm)' }}>
            Select a confirmed event. This creates an engagement connecting {speaker.firstName} to that event
            {inPipeline && ' and moves their status to Active'}.
          </p>
          <FormField label="Event" required id="bookEvent">
            <Select id="bookEvent" value={bookEventId} onChange={(e) => setBookEventId(e.target.value)}>
              <option value="">Select event…</option>
              {confirmedEvents.map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.name} ({ev.date})</option>
              ))}
            </Select>
          </FormField>
          {confirmedEvents.length === 0 && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-subtle)' }}>No confirmed events yet.</p>
          )}
        </div>
      </Modal>
    </div>
  );
}
